import { UUID } from './common.dto';

/**
 * Business units of the project processing journey.
 * Product page: admin «Поток проекта».
 */
export enum ProjectJourneyUnit {
  PROJECT = 'project',
  UPLOAD = 'upload',
  FACES = 'faces',
  SCENES = 'scenes',
  CLUSTERING = 'clustering',
  ALBUMS = 'albums',
}

/** Normalized pipeline milestones; RU labels live on the frontend dictionary, not in the contract. */
export enum ProjectJourneyEventKind {
  FILE_UPLOADED = 'fileUploaded',
  IMAGE_ANALYZED = 'imageAnalyzed',
  FACE_RECOGNIZED = 'faceRecognized',
  PERSON_CREATED = 'personCreated',
  SCENE_EMBEDDING = 'sceneEmbedding',
  SCENE_DETECTION_COMPLETED = 'sceneDetectionCompleted',
  PERSONS_CLUSTERING_STARTED = 'personsClusteringStarted',
  PERSONS_CLUSTERED = 'personsClustered',
  BOOK_ASSEMBLY_STARTED = 'bookAssemblyStarted',
  BOOK_ASSEMBLY_COMPLETED = 'bookAssemblyCompleted',
  BOOK_ASSEMBLY_FAILED = 'bookAssemblyFailed',
  // Failure milestones of the upload/faces/scenes stages, one per orchestrator process stage. Sourced
  // from the orchestrator's process table (the only place every failure of these stages lands), not
  // from per-service topics — so the feed, the metrics, and the snapshot count the same rows.
  FILE_PROCESS_FAILED = 'fileProcessFailed',
  FACE_RECOGNIZE_FAILED = 'faceRecognizeFailed',
  FACE_POST_PROCESS_FAILED = 'facePostProcessFailed',
  SCENE_EMBEDDING_FAILED = 'sceneEmbeddingFailed',
  IMAGE_ANALYZE_FAILED = 'imageAnalyzeFailed',
  SCENE_DETECTION_FAILED = 'sceneDetectionFailed',
}

export type ProjectJourneyEventStatus = 'ok' | 'failed';

/** Sentinel label of the breakdown tail: unrecognized reasons plus everything beyond the top entries. */
export const PROJECT_JOURNEY_FAIL_REASON_OTHER: string = 'other';

/** One canonical failure reason of a unit with how many processes died of it. */
export interface ProjectJourneyFailReason {
  reason: string;
  count: number;
}

/** One live hop pushed over Socket.IO room `project:journey:operations:{projectId}`. */
export interface ProjectJourneyEvent {
  projectId: UUID;
  unit: ProjectJourneyUnit;
  kind: ProjectJourneyEventKind;
  count: number;
  status: ProjectJourneyEventStatus;
  /** ISO timestamp of the source Kafka event processing moment. */
  ts: string;
  /**
   * Trace of the chain that produced this hop, for the drill-down link. Optional by design: a
   * snapshot row has none, and a chain whose trace context was lost on the way still has to deliver
   * its event.
   */
  traceId?: string;
}

export interface ProjectJourneyUnitSnapshot {
  unit: ProjectJourneyUnit;
  /** Accumulated counters keyed by ProjectJourneyEventKind. */
  counters: Partial<Record<ProjectJourneyEventKind, number>>;
  /**
   * How much broke in this unit overall — a deliberately combined metric, not a count of one entity:
   * for FACES it is failed recognitions (per file) plus failed post-processings (per face); for SCENES
   * it is scene analyze, image analyze, and scene detection combined. Reads as "is something wrong here
   * and how badly", not as "this many files were affected".
   *
   * For ALBUMS the count only accumulates from the point book assembly started publishing events: a
   * project assembled earlier reads zero as "no trace left", not "went through without a single error".
   */
  failed: number;
  /**
   * Retrospective breakdown of `failed` by canonical reason, sorted by count descending: top entries
   * plus one aggregated tail, together summing to the counter. Only allow-listed labels appear here —
   * raw failure texts never leave the orchestrator. Absent for units whose errors carry no reasons
   * (ALBUMS, PROJECT) and when the orchestrator predates the breakdown (rolling deploy).
   */
  topFailReasons?: ProjectJourneyFailReason[];
}

/** Late-join bootstrap returned by GET project-journey/:projectId/snapshot. */
export interface ProjectJourneySnapshot {
  projectId: UUID;
  units: ProjectJourneyUnitSnapshot[];
  generatedAt: string;
  /**
   * Snapshot authority. The photo pipeline comes from the orchestrator's process tree; album assembly
   * never runs inside that tree, so its numbers come from the albums of the project plus the stored
   * journal events.
   */
  source: 'orchestrator+albums';
}

/** Deep-scroll page returned by GET project-journey/:projectId/history, newest event first. */
export interface ProjectJourneyHistory {
  projectId: UUID;
  events: ProjectJourneyEvent[];
  /** How long a returned row is guaranteed to still exist, so the UI can label the feed's floor. */
  retentionDays: number;
}
